import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiErrorDetail } from "../lib/api";
import EmployerLayout from "./EmployerLayout";
import { useProfessions } from "../hooks/useProfessions";
import { useAppT } from "../components/home/i18n";
import { Search, Lock, Star, X, MapPin, ShieldCheck } from "lucide-react";
import { AuthedVideo } from "../components/AuthedMedia";

function DetailModal({ id, onClose, onShortlist, a }) {
  const [d, setD] = useState(null);
  useEffect(() => { API.get(`/talent/${id}`).then(({ data }) => setD(data)).catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); onClose(); }); }, [id]);
  if (!d) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" data-testid="talent-detail-modal">
      <div className="my-8 w-full max-w-2xl rounded-md bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-[#E8E4DB] px-6 py-4">
          <div className="font-serif text-2xl text-[#173A2C]">{d.alias}</div>
          <button onClick={onClose} data-testid="close-talent-modal"><X className="h-5 w-5 text-[#5C5855]" /></button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto thin-scroll p-6">
          <div className="flex flex-wrap gap-1.5">{d.professions.map((p) => <span key={p} className="rounded-full bg-[#C4A46B]/15 px-2.5 py-0.5 text-xs text-[#8a6f38]">{p}</span>)}</div>
          <div className="mt-2 text-[#2C2A29]">{d.headline}</div>
          <div className="mt-1 flex items-center gap-1 text-sm text-[#5C5855]"><MapPin className="h-3.5 w-3.5" /> {d.broad_location} · {d.experience_years ?? "?"} {a.common.years} · {d.availability}</div>
          {d.bio && <p className="mt-2 text-sm text-[#5C5855]">{d.bio}</p>}
          <div className="mt-3">
            {Object.entries(d.role_skills || {}).map(([p, sk]) => <div key={p} className="text-sm"><b className="text-[#1F4A38]">{p}:</b> <span className="text-[#5C5855]">{sk.join(", ")}</span></div>)}
          </div>
          <div className="mt-3 text-sm text-[#5C5855]">{a.empSearch.languagesWord}: {d.languages.map((l) => `${l.lang} (${l.level})`).join(", ") || "—"}</div>

          {d.videos?.length > 0 && (
            <div className="mt-4"><div className="text-[11px] font-semibold uppercase tracking-wide text-[#C4A46B]">{a.empSearch.videoPreviews}</div>
              <div className="mt-2 flex flex-wrap gap-3">
                {d.videos.map((v) => <AuthedVideo key={v.id} testid={`video-${v.id}`} path={`/talent/${d.id}/media/${v.id}`} className="h-28 w-44 rounded-sm bg-black" />)}
              </div>
            </div>
          )}
          {d.certificates?.length > 0 && (
            <div className="mt-4"><div className="text-[11px] font-semibold uppercase tracking-wide text-[#C4A46B]">{a.empSearch.certificates}</div>
              <div data-testid="cert-disabled" className="mt-2 flex items-center gap-1 rounded-sm border border-[#E8E4DB] bg-[#FDFBF7] px-3 py-2 text-xs text-[#5C5855]"><Lock className="h-3.5 w-3.5" /> {d.certificates.length} {a.empSearch.certDisabled}</div>
            </div>
          )}
          <div className="mt-4 flex items-start gap-2 rounded-sm border border-[#1F4A38]/20 bg-[#1F4A38]/6 p-3 text-xs text-[#2C2A29]">
            <ShieldCheck className="mt-0.5 h-4 w-4 text-[#1F4A38]" /> {a.empSearch.privacyNote}
          </div>
          <button data-testid="shortlist-btn" onClick={() => onShortlist(d.id)} className="mt-4 inline-flex items-center gap-1 rounded-full bg-[#C4A46B] px-5 py-2.5 text-sm font-semibold text-[#173A2C]"><Star className="h-4 w-4" /> {a.empSearch.addShortlist}</button>
        </div>
      </div>
    </div>
  );
}

export default function EmployerSearch() {
  const professions = useProfessions();
  const { a } = useAppT();
  const [gate, setGate] = useState({ ok: true, status: "verified", reason: null });
  const [filters, setFilters] = useState({ profession: "", skill: "", language: "", country: "", availability: "", q: "" });
  const [results, setResults] = useState([]);
  const [openId, setOpenId] = useState(null);

  const run = async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    try {
      const { data } = await API.get("/talent/search", { params });
      setResults(data.results); setGate({ ok: true });
    } catch (e) {
      if (e.response?.status === 403) setGate({ ok: false, status: "blocked", reason: formatApiErrorDetail(e.response?.data?.detail) });
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { run(); }, []);

  const shortlist = async (pid) => {
    try { await API.post("/employer/shortlist", { profile_id: pid }); toast.success(a.empSearch.addedShortlist); setOpenId(null); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!gate.ok) return (
    <EmployerLayout>
      <h1 className="font-serif text-3xl text-[#173A2C]">{a.empSearch.title}</h1>
      <div className="mt-4 rounded-md border border-[#8B3A3A]/30 bg-[#8B3A3A]/6 p-4 text-sm text-[#8B3A3A]" data-testid="search-blocked">{gate.reason}</div>
    </EmployerLayout>
  );

  const inputCls = "rounded-sm border border-[#E8E4DB] bg-white px-3 py-2 text-sm outline-none focus:border-[#1F4A38]";
  return (
    <EmployerLayout>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-serif text-3xl text-[#173A2C]">{a.empSearch.title}</h1>
        <span className="text-xs text-[#5C5855]">{a.empSearch.approvedOnly}</span>
      </div>

      <div className="mt-4 grid gap-2 rounded-md border border-[#E8E4DB] bg-white p-4 sm:grid-cols-3 lg:grid-cols-6">
        <select data-testid="filter-profession" className={inputCls} value={filters.profession} onChange={(e) => setFilters({ ...filters, profession: e.target.value })}><option value="">{a.empSearch.anyProfession}</option>{professions.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}</select>
        <input data-testid="filter-skill" placeholder={a.empSearch.skill} className={inputCls} value={filters.skill} onChange={(e) => setFilters({ ...filters, skill: e.target.value })} />
        <input data-testid="filter-language" placeholder={a.empSearch.language} className={inputCls} value={filters.language} onChange={(e) => setFilters({ ...filters, language: e.target.value })} />
        <input data-testid="filter-country" placeholder={a.empSearch.country} className={inputCls} value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} />
        <input data-testid="filter-q" placeholder={a.empSearch.keyword} className={inputCls} value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
        <button data-testid="search-btn" onClick={run} className="inline-flex items-center justify-center gap-1 rounded-sm bg-[#1F4A38] px-4 py-2 text-sm font-semibold text-white"><Search className="h-4 w-4" /> {a.empSearch.search}</button>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="talent-results">
        {results.length === 0 && <div className="text-sm text-[#5C5855]">{a.empSearch.noMatch}</div>}
        {results.map((r) => (
          <div key={r.id} data-testid={`talent-card-${r.alias}`} className="rounded-md border border-[#E8E4DB] bg-white p-4">
            <div className="flex items-center justify-between"><span className="font-semibold text-[#173A2C]">{r.alias}</span><Lock className="h-4 w-4 text-[#8a6f38]" /></div>
            <div className="mt-1 text-sm text-[#2C2A29]">{r.headline}</div>
            <div className="text-xs text-[#5C5855]">{r.broad_location} · {r.experience_years ?? "?"} {a.common.years} · {r.availability}</div>
            <div className="mt-2 flex flex-wrap gap-1">{r.professions.map((p) => <span key={p} className="rounded-full bg-[#1F4A38]/6 px-2 py-0.5 text-[11px] text-[#1F4A38]">{p}</span>)}</div>
            <button data-testid={`view-${r.alias}`} onClick={() => setOpenId(r.id)} className="mt-3 w-full rounded-sm bg-[#1F4A38] py-2 text-sm font-semibold text-white">{a.empSearch.view}</button>
          </div>
        ))}
      </div>

      {openId && <DetailModal id={openId} onClose={() => setOpenId(null)} onShortlist={shortlist} a={a} />}
    </EmployerLayout>
  );
}
